/**
 * VorgangDetailPage - Klient-spezifische Vorgang-Detailansicht
 * Zeigt den einzelnen Vorgang eines Betreuten mit Bescheiden, Aktenzeichen, Korrespondenz
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabParams } from '@/hooks/useTabRoute';
import { useTabs } from '@/contexts/TabContext';
import {
  ArrowLeft, FileText, Building2, Calendar, Euro, Hash,
  Eye, X, Edit, Save, Loader2, Clock, ExternalLink,
  Mail, ChevronRight, RefreshCw, Plus, AlertTriangle,
  Trash2, History, CircleDot, ArrowRight, Phone, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import EntityPicker from '@/components/EntityPicker';
import { holePreviewUrl } from '@/lib/downloadTicket';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const STATUS_COLORS = {
  aktiv: 'bg-green-100 text-green-700',
  ruhend: 'bg-yellow-100 text-yellow-700',
  beendet: 'bg-slate-100 text-slate-600',
};

export default function VorgangDetailPage() {
  const navigate = useNavigate();
  const { id: klientId, vorgangId } = useTabParams();
  const { activeTabId, updateTabTitle } = useTabs();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showAddVerlauf, setShowAddVerlauf] = useState(false);
  const [verlaufForm, setVerlaufForm] = useState({ typ: 'sonstiges', titel: '', beschreibung: '', datum: new Date().toISOString().slice(0, 10), betrag: '', ergebnis: '' });
  const [savingVerlauf, setSavingVerlauf] = useState(false);
  const [verlaufTypen, setVerlaufTypen] = useState({});

  useEffect(() => { if (klientId && vorgangId) loadData(); }, [klientId, vorgangId]);

  // Ticket-basierte Vorschau-URL (Live Preview Auth Gap, 25.09.2026): der
  // iframe kann keinen Authorization-Header setzen -- Ticket vorab anfordern
  // statt der zuvor komplett unauthentifizierten Backend-URL.
  useEffect(() => {
    let aktuell = true;
    if (!previewDoc) { setPreviewUrl(null); return; }
    const token = localStorage.getItem('olympus_token');
    holePreviewUrl(
      `${API}/api/betreuung/dokumente/${previewDoc.id}/download-ticket`,
      `${API}/api/betreuung/dokumente/${previewDoc.id}/download?inline=true`,
      { 'Authorization': `Bearer ${token}` }
    )
      .then(url => { if (aktuell) setPreviewUrl(url); })
      .catch(() => { if (aktuell) { toast.error('Vorschau konnte nicht geladen werden'); setPreviewDoc(null); } });
    return () => { aktuell = false; };
  }, [previewDoc]);

  // Tab-Titel aktualisieren
  useEffect(() => {
    if (data && activeTabId) {
      const klientName = data.klient ? `${data.klient.vorname || ''} ${data.klient.nachname || ''}`.trim() : '';
      const typLabel = data.typ_label || data.bezeichnung || 'Vorgang';
      updateTabTitle(activeTabId, klientName ? `Vorgang: ${klientName}` : typLabel);
    }
  }, [data, activeTabId, updateTabTitle]);

  async function loadData() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/vorgaenge/klient/${klientId}/${vorgangId}`);
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setEditForm({
          aktenzeichen: d.aktenzeichen || '',
          sachbearbeiter: d.sachbearbeiter || '',
          institution: d.institution || '',
          institution_id: d.institution_id || '',
          sachbearbeiter_id: d.sachbearbeiter_id || '',
          ansprechpartner_id: d.ansprechpartner_id || '',
          // Resolved entities for display
          institution_resolved: d.institution_resolved || null,
          sachbearbeiter_resolved: d.sachbearbeiter_resolved || null,
          ansprechpartner_resolved: d.ansprechpartner_resolved || null,
          betrag: d.betrag || '',
          turnus: d.turnus || 'monatlich',
          status: d.status || 'aktiv',
          notizen: d.notizen || '',
          beginn_datum: d.beginn_datum || '',
          ende_datum: d.ende_datum || '',
        });
      }
      // Verlauf-Typen laden
      const vRes = await fetch(`${API}/api/vorgaenge/klient/${klientId}/${vorgangId}/verlauf`);
      if (vRes.ok) {
        const vData = await vRes.json();
        setVerlaufTypen(vData.typen || {});
      }
    } catch (e) { console.error(e); toast.error('Fehler beim Laden'); }
    setLoading(false);
  }

  async function saveChanges() {
    setSaving(true);
    try {
      // Strip resolved objects before saving - only IDs and display names go to DB
      const { institution_resolved, sachbearbeiter_resolved, ansprechpartner_resolved, ...payload } = editForm;
      const res = await fetch(`${API}/api/vorgaenge/klient/${klientId}/${vorgangId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setEditing(false);
        loadData();
        toast.success('Gespeichert');
      }
    } catch (e) { toast.error('Fehler beim Speichern'); }
    setSaving(false);
  }

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (!data) {
    return <div className="p-6"><p className="text-slate-500">Vorgang nicht gefunden</p></div>;
  }

  async function addVerlaufEintrag() {
    setSavingVerlauf(true);
    try {
      const payload = { ...verlaufForm };
      if (!payload.titel) payload.titel = verlaufTypen[payload.typ] || payload.typ;
      if (payload.betrag === '') delete payload.betrag;
      const res = await fetch(`${API}/api/vorgaenge/klient/${klientId}/${vorgangId}/verlauf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setShowAddVerlauf(false);
        setVerlaufForm({ typ: 'sonstiges', titel: '', beschreibung: '', datum: new Date().toISOString().slice(0, 10), betrag: '', ergebnis: '' });
        loadData();
        toast.success('Eintrag hinzugefügt');
      }
    } catch (e) { toast.error('Fehler'); }
    setSavingVerlauf(false);
  }

  async function deleteVerlaufEintrag(eintragId) {
    try {
      await fetch(`${API}/api/vorgaenge/klient/${klientId}/${vorgangId}/verlauf/${eintragId}`, { method: 'DELETE' });
      loadData();
      toast.success('Eintrag gelöscht');
    } catch (e) { toast.error('Fehler'); }
  }

  const klient = data.klient || {};
  const klientName = `${klient.vorname || ''} ${klient.nachname || ''}`.trim();

  return (
    <div className="space-y-5 max-w-4xl mx-auto" data-testid="vorgang-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-slate-800 truncate">{data.typ_label || data.bezeichnung}</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {klientName && <span className="font-medium">{klientName}</span>}
            {data.aktenzeichen && <span className="ml-2 font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{data.aktenzeichen}</span>}
          </p>
        </div>
        <Badge className={STATUS_COLORS[data.status] || STATUS_COLORS.aktiv} data-testid="vorgang-status">
          {data.status || 'aktiv'}
        </Badge>
        <Button variant="outline" size="sm" onClick={() => setEditing(!editing)} data-testid="edit-toggle">
          <Edit className="w-4 h-4 mr-1" /> {editing ? 'Abbrechen' : 'Bearbeiten'}
        </Button>
      </div>

      {/* Stammdaten */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Hash className="w-4 h-4 text-blue-500" />
            Vorgangsdaten
          </CardTitle>
        </CardHeader>
        <CardContent>
          {editing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Aktenzeichen</Label>
                <Input value={editForm.aktenzeichen} onChange={e => setEditForm(f => ({ ...f, aktenzeichen: e.target.value }))} data-testid="edit-aktenzeichen" />
              </div>
              <EntityPicker
                entityType="institution"
                label="Träger / Institution"
                value={editForm.institution_id ? {
                  id: editForm.institution_id,
                  name: editForm.institution_resolved?.name || editForm.institution || '',
                  subtext: editForm.institution_resolved ? `${editForm.institution_resolved.typ || ''} · ${editForm.institution_resolved.ort || ''}`.replace(/^ · | · $/g, '') : ''
                } : null}
                onChange={(entity) => {
                  if (entity) {
                    setEditForm(f => ({
                      ...f,
                      institution_id: entity.id,
                      institution: entity.name,
                      institution_resolved: entity,
                      // Reset Ansprechpartner wenn Institution wechselt
                      ansprechpartner_id: '',
                      ansprechpartner_resolved: null,
                    }));
                  } else {
                    setEditForm(f => ({
                      ...f,
                      institution_id: '',
                      institution: '',
                      institution_resolved: null,
                      ansprechpartner_id: '',
                      ansprechpartner_resolved: null,
                    }));
                  }
                }}
              />
              <EntityPicker
                entityType="person"
                label="Sachbearbeiter/in"
                value={editForm.sachbearbeiter_id ? {
                  id: editForm.sachbearbeiter_id,
                  name: editForm.sachbearbeiter_resolved?.name || editForm.sachbearbeiter || '',
                  subtext: editForm.sachbearbeiter_resolved?.rolle || ''
                } : null}
                onChange={(entity) => {
                  if (entity) {
                    setEditForm(f => ({
                      ...f,
                      sachbearbeiter_id: entity.id,
                      sachbearbeiter: entity.name,
                      sachbearbeiter_resolved: entity,
                    }));
                  } else {
                    setEditForm(f => ({ ...f, sachbearbeiter_id: '', sachbearbeiter: '', sachbearbeiter_resolved: null }));
                  }
                }}
              />
              <EntityPicker
                entityType="ansprechpartner"
                label="Ansprechpartner/in"
                institutionId={editForm.institution_id || undefined}
                value={editForm.ansprechpartner_id ? {
                  id: editForm.ansprechpartner_id,
                  name: editForm.ansprechpartner_resolved?.name || '',
                  subtext: editForm.ansprechpartner_resolved ? `${editForm.ansprechpartner_resolved.rolle || ''} ${editForm.ansprechpartner_resolved.firma_name ? `bei ${editForm.ansprechpartner_resolved.firma_name}` : ''}`.trim() : ''
                } : null}
                onChange={(entity) => {
                  if (entity) {
                    setEditForm(f => ({
                      ...f,
                      ansprechpartner_id: entity.id,
                      ansprechpartner_resolved: entity,
                    }));
                  } else {
                    setEditForm(f => ({ ...f, ansprechpartner_id: '', ansprechpartner_resolved: null }));
                  }
                }}
              />
              <div className="space-y-1.5">
                <Label>Betrag (€)</Label>
                <Input type="number" step="0.01" value={editForm.betrag} onChange={e => setEditForm(f => ({ ...f, betrag: parseFloat(e.target.value) || '' }))} data-testid="edit-betrag" />
              </div>
              <div className="space-y-1.5">
                <Label>Turnus</Label>
                <Input value={editForm.turnus} onChange={e => setEditForm(f => ({ ...f, turnus: e.target.value }))} data-testid="edit-turnus" />
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <select className="w-full border rounded-md p-2 text-sm" value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))} data-testid="edit-status">
                  <option value="aktiv">Aktiv</option>
                  <option value="ruhend">Ruhend</option>
                  <option value="beendet">Beendet</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Beginn</Label>
                <Input type="date" value={editForm.beginn_datum} onChange={e => setEditForm(f => ({ ...f, beginn_datum: e.target.value }))} data-testid="edit-beginn" />
              </div>
              <div className="space-y-1.5">
                <Label>Ende</Label>
                <Input type="date" value={editForm.ende_datum} onChange={e => setEditForm(f => ({ ...f, ende_datum: e.target.value }))} data-testid="edit-ende" />
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <Label>Notizen</Label>
                <Textarea value={editForm.notizen} onChange={e => setEditForm(f => ({ ...f, notizen: e.target.value }))} rows={3} data-testid="edit-notizen" />
              </div>
              <div className="sm:col-span-2 flex justify-end">
                <Button onClick={saveChanges} disabled={saving} data-testid="save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Speichern
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-6 text-sm">
              <InfoField label="Aktenzeichen" value={data.aktenzeichen} mono />
              <InfoField
                label="Träger / Institution"
                value={data.institution_resolved?.name || data.institution}
                icon={<Building2 className="w-3.5 h-3.5" />}
                linked={data.institution_id ? true : false}
                subtext={data.institution_resolved ? `${data.institution_resolved.typ || ''} · ${data.institution_resolved.ort || ''}`.replace(/^ · | · $/g, '') : ''}
              />
              <InfoField
                label="Sachbearbeiter/in"
                value={data.sachbearbeiter_resolved?.name || data.sachbearbeiter}
                subtext={data.sachbearbeiter_resolved?.rolle || ''}
                linked={data.sachbearbeiter_id ? true : false}
              />
              {(data.ansprechpartner_id || data.ansprechpartner_resolved) && (
                <InfoField
                  label="Ansprechpartner/in"
                  value={data.ansprechpartner_resolved?.name || ''}
                  subtext={data.ansprechpartner_resolved ? `${data.ansprechpartner_resolved.rolle || ''} ${data.ansprechpartner_resolved.firma_name ? `bei ${data.ansprechpartner_resolved.firma_name}` : ''}`.trim() : ''}
                  linked
                />
              )}
              <InfoField label="Betrag" value={data.betrag != null ? `${Number(data.betrag).toLocaleString('de-DE')} €` : '–'} icon={<Euro className="w-3.5 h-3.5" />} />
              <InfoField label="Turnus" value={data.turnus || 'monatlich'} />
              <InfoField label="Status" value={data.status || 'aktiv'} />
              <InfoField label="Beginn" value={data.beginn_datum} icon={<Calendar className="w-3.5 h-3.5" />} />
              <InfoField label="Ende" value={data.ende_datum} icon={<Calendar className="w-3.5 h-3.5" />} />
              {data.notizen && (
                <div className="col-span-full">
                  <p className="text-xs text-slate-400 mb-0.5">Notizen</p>
                  <p className="text-slate-600 whitespace-pre-wrap">{data.notizen}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bescheide / Dokumente */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-green-500" />
            Bescheide & Dokumente
            <Badge variant="outline" className="ml-auto text-xs">{(data.alle_bescheide?.length || 0) + (data.kategorie_dokumente?.length || 0)}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Aktueller Bescheid */}
          {data.neuester_bescheid ? (
            <div className="mb-4">
              <p className="text-xs font-medium text-slate-500 mb-1.5">Aktueller Bescheid</p>
              <button
                onClick={() => setPreviewDoc(data.neuester_bescheid)}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-100 hover:border-green-300 transition-colors text-left"
                data-testid="neuester-bescheid"
              >
                <FileText className="w-5 h-5 text-green-600 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-700 truncate">{data.neuester_bescheid.dateiname || data.neuester_bescheid.titel || 'Bescheid'}</p>
                  <p className="text-xs text-slate-400">
                    {data.neuester_bescheid.created_date && new Date(data.neuester_bescheid.created_date).toLocaleDateString('de-DE')}
                    {data.neuester_bescheid.betrag && ` · ${Number(data.neuester_bescheid.betrag).toLocaleString('de-DE')} €`}
                  </p>
                </div>
                <Eye className="w-4 h-4 text-green-400 shrink-0" />
              </button>
            </div>
          ) : (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-100 rounded-lg flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
              <p className="text-sm text-amber-700">Kein Bescheid zugeordnet</p>
            </div>
          )}

          {/* Alle Bescheide */}
          {data.alle_bescheide?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium text-slate-400 mb-1.5">Alle Bescheide ({data.alle_bescheide.length})</p>
              <div className="space-y-1">
                {data.alle_bescheide.map((doc, i) => (
                  <button
                    key={doc.id || i}
                    onClick={() => setPreviewDoc(doc)}
                    className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-slate-50 transition-colors text-left text-sm"
                    data-testid={`bescheid-${i}`}
                  >
                    <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="truncate flex-1 text-slate-600">{doc.dateiname || doc.titel || 'Dokument'}</span>
                    <span className="text-xs text-slate-300 shrink-0">
                      {doc.created_date && new Date(doc.created_date).toLocaleDateString('de-DE')}
                    </span>
                    <Eye className="w-3 h-3 text-slate-300 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Kategorie-Dokumente */}
          {data.kategorie_dokumente?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-400 mb-1.5">Weitere Dokumente ({data.kategorie_dokumente.length})</p>
              <div className="space-y-1">
                {data.kategorie_dokumente.map((doc, i) => (
                  <button
                    key={doc.id || i}
                    onClick={() => setPreviewDoc(doc)}
                    className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-slate-50 transition-colors text-left text-sm"
                    data-testid={`kat-doc-${i}`}
                  >
                    <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="truncate flex-1 text-slate-600">{doc.dateiname || doc.name || 'Dokument'}</span>
                    <Eye className="w-3 h-3 text-slate-300 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {!data.alle_bescheide?.length && !data.kategorie_dokumente?.length && !data.neuester_bescheid && (
            <p className="text-sm text-slate-400 text-center py-4">Keine Dokumente vorhanden</p>
          )}
        </CardContent>
      </Card>

      {/* Korrespondenz mit dem Träger */}
      {data.korrespondenz?.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-4 h-4 text-indigo-500" />
              Korrespondenz mit {data.institution || 'Träger'}
              <Badge variant="outline" className="ml-auto text-xs">{data.korrespondenz.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {data.korrespondenz.map((k, i) => (
                <div key={k.id || i} className="flex items-center gap-2 p-2 rounded-md hover:bg-slate-50 text-sm">
                  <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-slate-700 truncate">{k.betreff || k.titel || 'Schreiben'}</p>
                    <p className="text-xs text-slate-400">{k.datum && new Date(k.datum).toLocaleDateString('de-DE')}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Verlauf / Timeline */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4 text-purple-500" />
              Verlauf / Timeline
              {data.verlauf?.length > 0 && <Badge variant="outline" className="ml-1 text-xs">{data.verlauf.length}</Badge>}
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => setShowAddVerlauf(!showAddVerlauf)} data-testid="add-verlauf-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Eintrag
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Neuer Eintrag Form */}
          {showAddVerlauf && (
            <div className="mb-5 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
              <p className="text-sm font-medium text-slate-700">Neuen Eintrag hinzufügen</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Typ</Label>
                  <select
                    className="w-full border rounded-md p-2 text-sm bg-white"
                    value={verlaufForm.typ}
                    onChange={e => {
                      const t = e.target.value;
                      setVerlaufForm(f => ({ ...f, typ: t, titel: verlaufTypen[t] || '' }));
                    }}
                    data-testid="verlauf-typ-select"
                  >
                    {Object.entries(verlaufTypen).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Datum</Label>
                  <Input type="date" value={verlaufForm.datum} onChange={e => setVerlaufForm(f => ({ ...f, datum: e.target.value }))} data-testid="verlauf-datum" />
                </div>
                <div className="sm:col-span-2 space-y-1">
                  <Label className="text-xs">Titel</Label>
                  <Input value={verlaufForm.titel} onChange={e => setVerlaufForm(f => ({ ...f, titel: e.target.value }))} placeholder={verlaufTypen[verlaufForm.typ] || 'Titel'} data-testid="verlauf-titel" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Betrag (falls relevant)</Label>
                  <Input type="number" step="0.01" value={verlaufForm.betrag} onChange={e => setVerlaufForm(f => ({ ...f, betrag: e.target.value }))} placeholder="z.B. 450.00" data-testid="verlauf-betrag" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Ergebnis</Label>
                  <Input value={verlaufForm.ergebnis} onChange={e => setVerlaufForm(f => ({ ...f, ergebnis: e.target.value }))} placeholder="z.B. bewilligt, abgelehnt" data-testid="verlauf-ergebnis" />
                </div>
                <div className="sm:col-span-2 space-y-1">
                  <Label className="text-xs">Beschreibung</Label>
                  <Textarea value={verlaufForm.beschreibung} onChange={e => setVerlaufForm(f => ({ ...f, beschreibung: e.target.value }))} rows={2} placeholder="Details..." data-testid="verlauf-beschreibung" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setShowAddVerlauf(false)}>Abbrechen</Button>
                <Button size="sm" onClick={addVerlaufEintrag} disabled={savingVerlauf} data-testid="save-verlauf-btn">
                  {savingVerlauf ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                  Hinzufügen
                </Button>
              </div>
            </div>
          )}

          {/* Timeline */}
          {data.verlauf?.length > 0 ? (
            <div className="relative">
              {/* Vertikale Linie */}
              <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-slate-200" />

              <div className="space-y-0">
                {data.verlauf.map((e, i) => (
                  <div key={e.id || i} className="relative flex gap-4 pb-5 group" data-testid={`verlauf-eintrag-${i}`}>
                    {/* Timeline-Punkt */}
                    <div className="relative z-10 shrink-0">
                      <div className={`w-[31px] h-[31px] rounded-full flex items-center justify-center border-2 ${
                        i === 0 ? 'bg-purple-500 border-purple-500 text-white' : 'bg-white border-slate-300 text-slate-400'
                      }`}>
                        <VerlaufIcon typ={e.typ} />
                      </div>
                    </div>

                    {/* Inhalt */}
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className={`text-sm font-medium ${i === 0 ? 'text-slate-800' : 'text-slate-600'}`}>
                            {e.titel || verlaufTypen[e.typ] || e.typ}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {e.datum && new Date(e.datum).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
                            {e.betrag != null && e.betrag !== '' && <span className="ml-2 font-mono text-slate-500">{Number(e.betrag).toLocaleString('de-DE')} €</span>}
                            {e.ergebnis && <Badge variant="outline" className="ml-2 text-[10px] py-0">{e.ergebnis}</Badge>}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                          onClick={() => deleteVerlaufEintrag(e.id)}
                          data-testid={`delete-verlauf-${i}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      {e.beschreibung && (
                        <p className="text-sm text-slate-500 mt-1 whitespace-pre-wrap">{e.beschreibung}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <History className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">Noch keine Einträge</p>
              <p className="text-xs text-slate-300 mt-0.5">Dokumentieren Sie den Verlauf dieses Vorgangs</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vorschau-Dialog */}
      {previewDoc && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setPreviewDoc(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <p className="font-semibold text-slate-800 truncate">{previewDoc.dateiname || previewDoc.titel || 'Dokument'}</p>
              <Button size="sm" variant="ghost" onClick={() => setPreviewDoc(null)} data-testid="preview-close">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 min-h-0 p-1">
              {previewUrl ? (
                <iframe
                  src={previewUrl}
                  className="w-full h-full min-h-[70vh] rounded"
                  title="Dokumenten-Vorschau"
                />
              ) : (
                <div className="w-full h-full min-h-[70vh] flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoField({ label, value, mono, icon, linked, subtext }) {
  return (
    <div>
      <p className="text-xs text-slate-400 mb-0.5">{label}</p>
      <div className="flex items-center gap-1.5">
        {icon && <span className="text-slate-400">{icon}</span>}
        <div>
          <p className={`text-slate-700 ${mono ? 'font-mono text-xs' : ''} ${linked ? 'text-blue-600' : ''}`}>{value || '–'}</p>
          {subtext && <p className="text-xs text-slate-400">{subtext}</p>}
        </div>
      </div>
    </div>
  );
}

function VerlaufIcon({ typ }) {
  const size = 'w-3.5 h-3.5';
  switch (typ) {
    case 'antrag_gestellt': return <FileText className={size} />;
    case 'bescheid_erhalten': return <FileText className={size} />;
    case 'widerspruch': return <AlertTriangle className={size} />;
    case 'klage': return <AlertTriangle className={size} />;
    case 'weiterbewilligung': return <RefreshCw className={size} />;
    case 'aenderungsbescheid': return <Edit className={size} />;
    case 'telefonat': return <Phone className={size} />;
    case 'email': return <Mail className={size} />;
    case 'frist': return <Clock className={size} />;
    case 'beratung': return <MessageSquare className={size} />;
    default: return <CircleDot className={size} />;
  }
}
